// Cobalt's own crash + failed-transaction reporting. Reports go to this app's
// server log only — no third party. Seed phrases, passcodes and vault material
// are never included: only the error text, screen, and ledger result data.

const ENDPOINT = "/api/public/client-report";
const RELEASE = import.meta.env["VITE_APP_RELEASE"] ?? "cobalt-wallet";

const SECRET_PATTERNS: RegExp[] = [
  /\b(?:[a-z]{3,8}\s+){11,23}[a-z]{3,8}\b/gi, // mnemonic-looking word runs
  /\bs[1-9A-HJ-NP-Za-km-z]{25,}\b/g, // XRPL family seeds
  /\b\d{6}\b/g, // 6-digit passcodes
];

function scrub(value: string | undefined): string | undefined {
  if (!value) return undefined;
  let out = value;
  for (const pattern of SECRET_PATTERNS) out = out.replace(pattern, "[redacted]");
  return out.slice(0, 6_000);
}

interface ReportPayload {
  kind: "crash" | "transaction";
  message: string;
  route?: string;
  stack?: string;
  mechanism?: string;
  release?: string;
  address?: string;
  txType?: string;
  resultCode?: string;
  txHash?: string;
  userAgent?: string;
}

const recent = new Map<string, number>();
const DEDUPE_MS = 10_000;

function send(payload: ReportPayload) {
  if (typeof window === "undefined") return;
  const key = `${payload.kind}:${payload.message}`;
  const now = Date.now();
  const last = recent.get(key);
  if (last && now - last < DEDUPE_MS) return;
  recent.set(key, now);

  const body = JSON.stringify({
    ...payload,
    message: scrub(payload.message)?.slice(0, 500) ?? "Unknown error",
    ...(payload.stack ? { stack: scrub(payload.stack) } : {}),
    release: RELEASE,
    route: payload.route ?? window.location.pathname,
    userAgent: navigator.userAgent.slice(0, 300),
  });

  try {
    if (typeof navigator.sendBeacon === "function") {
      navigator.sendBeacon(ENDPOINT, new Blob([body], { type: "application/json" }));
      return;
    }
  } catch {
    // fall through to fetch
  }
  void fetch(ENDPOINT, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body,
    keepalive: true,
  }).catch(() => undefined);
}

function describe(error: unknown): { message: string; stack?: string } {
  if (error instanceof Error) {
    return { message: error.message || error.name, ...(error.stack ? { stack: error.stack } : {}) };
  }
  if (error instanceof Response) return { message: `Response ${error.status}` };
  return { message: typeof error === "string" ? error : JSON.stringify(error ?? "Unknown error") };
}

/** Reports an app crash or caught exception. */
export function reportCrash(error: unknown, mechanism = "manual") {
  const { message, stack } = describe(error);
  send({ kind: "crash", message, mechanism, ...(stack ? { stack } : {}) });
}

/** Reports a transaction that did not validate successfully on the ledger. */
export function reportTransactionFailure(info: {
  message: string;
  txType?: string;
  resultCode?: string;
  txHash?: string;
  address?: string;
}) {
  send({
    kind: "transaction",
    message: info.message,
    ...(info.txType ? { txType: info.txType } : {}),
    ...(info.resultCode ? { resultCode: info.resultCode } : {}),
    ...(info.txHash ? { txHash: info.txHash } : {}),
    ...(info.address ? { address: info.address } : {}),
  });
}

let installed = false;

/** Installs global crash listeners once, on the client. */
export function installTelemetry() {
  if (installed || typeof window === "undefined") return;
  installed = true;
  window.addEventListener("error", (event) => {
    reportCrash((event as ErrorEvent).error ?? (event as ErrorEvent).message, "onerror");
  });
  window.addEventListener("unhandledrejection", (event) => {
    reportCrash((event as PromiseRejectionEvent).reason, "unhandledrejection");
  });
}
